import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Building2, AlertTriangle, MessageSquare, Wrench } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

import { useDeviceType } from '@/hooks/useDeviceType';
import { cn } from '@/lib/utils';

const BottomTabBar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { isMobile, isTablet, isDesktop } = useDeviceType();

  const tabs = [
    {
      id: 'dashboard',
      label: 'Home',
      icon: Home,
      path: '/operations',
      badgeCount: 0,
    },
    {
      id: 'properties',
      label: 'Properties',
      icon: Building2,
      path: '/properties',
      badgeCount: 0,
    },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: AlertTriangle,
      path: '/alerts',
      badgeCount: 0,
    },
    {
      id: 'maintenance',
      label: 'Service',
      icon: Wrench,
      path: '/maintenance',
      badgeCount: 0,
    },
    {
      id: 'communications',
      label: 'Messages',
      icon: MessageSquare,
      path: '/communications',
      badgeCount: 0,
    },
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Hide on desktop, show on mobile and tablet
  if (!user || isDesktop) return null;

  return (
    <div className={cn(
      "fixed bottom-0 left-0 right-0 z-50 bg-background/95 border-t border-border backdrop-blur-sm safe-area-bottom",
      // Tablet: slightly larger, more padding
      isTablet ? "h-18" : "h-20"
    )}>
      <div className="grid grid-cols-5 h-full">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab.path);
          
          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={cn(
                "flex flex-col items-center justify-center gap-1 transition-all duration-200 touch-target",
                "active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                active
                  ? "text-primary bg-primary/10 border-t-2 border-primary"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/5",
                // Tablet: larger touch targets and icons
                isTablet && "gap-1.5"
              )}
            >
              <div className="relative">
                <Icon className={cn(
                  "h-5 w-5",
                  // Slightly larger icons on tablet
                  isTablet && "h-6 w-6"
                )} />
                {tab.badgeCount > 0 && (
                  <span className={cn(
                    "absolute -top-1.5 -right-2 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1",
                    isTablet && "min-w-[20px] h-[20px] text-[11px]"
                  )}>
                    {tab.badgeCount > 99 ? '99+' : tab.badgeCount}
                  </span>
                )}
              </div>
              <span className={cn(
                "text-xs font-medium truncate px-1",
                isTablet && "text-sm"
              )}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BottomTabBar;
